import { useParams } from 'react-router';
import { useBlockLayout } from 'react-table';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Box,
  BulkActionsContext,
  DashPlaceholder,
  ExpandableText,
  Flex,
  Image,
  PaginationContext,
  PaginationLoadMore,
  SortContext,
  SortHeader,
  Table,
  useScreenResolution,
} from 'dash-ui';
import {
  GetPropertyByIdQuery,
  useGetPortfolioPropertiesToCompareQuery,
} from 'gql/generated.graphql';
import {
  PropertyCompareItemsEnum,
  PROPERTY_COMPARISON_TABLE_COLUMNS,
} from 'containers/portfolio/PropertyComparisonModal/PropertyComparisonModal.constants';

const TableWrapper = styled(Box)`
  overflow: scroll;
  tbody {
    tr {
      td {
        display: flex !important;
        align-items: center;
      }
    }
  }
`;

const PropertyComparisonTable = ({
  selectedCompareOptions,
}: {
  selectedCompareOptions: Record<PropertyCompareItemsEnum, boolean>;
}) => {
  const { id: portfolioFolderId } = useParams();
  const { t } = useTranslation();
  const { setTotal, setPerPage, state } = useContext(PaginationContext);
  const { state: sortState } = useContext(SortContext);
  const { selectedItems } = useContext(BulkActionsContext);
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const variables = {
    portfolioIds: selectedItems.map((item) => item._id),
    portfolioFolderId,
    pagination: {
      limit: state.perPage,
    },
    sort: [sortState],
  };

  const { data, fetchMore, loading } = useGetPortfolioPropertiesToCompareQuery({
    variables,
  });
  const { properties, totalCount } = useMemo(
    () => ({
      properties: data?.getPortfolioPropertiesToCompare.collection || [],
      totalCount: data?.getPortfolioPropertiesToCompare.totalCount || 0,
    }),
    [data]
  );

  const columns = useMemo(() => {
    const dynamicColumns = Object.entries(selectedCompareOptions).reduce(
      (memo: any[], [key, value]) => {
        if (value) {
          const columns = PROPERTY_COMPARISON_TABLE_COLUMNS(t);
          const { title, accessor, Cell } =
            columns[key as PropertyCompareItemsEnum];

          memo.push({
            Header: () => (
              <SortHeader variation="green" accessor={accessor}>
                {t(title)}
              </SortHeader>
            ),
            accessor,
            name: accessor,
            minWidth: 200,
            ...(Cell && { Cell }),
          });
        }
        return memo;
      },
      []
    );

    return [
      {
        Header: () => (
          <SortHeader variation="green" accessor="name" isDefault>
            {t('pages:propertyPage.propertyName')}
          </SortHeader>
        ),
        width: 276,
        accessor: 'name',
        name: 'name',
        ...(isMobile ? { sticky: 'left', width: 199 } : {}),
        Cell: ({
          row: { original },
        }: {
          row: { original: GetPropertyByIdQuery['getPropertyById'] };
        }) => {
          const { mainPropertyMediaFile, name } = original;

          return (
            <Flex
              alignItems={isMobile ? 'start' : 'center'}
              flexDirection={isMobile ? 'column' : 'row'}
            >
              <Box mr={4}>
                {mainPropertyMediaFile ? (
                  <Image
                    src={
                      mainPropertyMediaFile?.files[0].thumbnailFile
                        ?.publicUrl || ''
                    }
                    size={80}
                  />
                ) : (
                  <DashPlaceholder variation="secondary" size={80} />
                )}
              </Box>
              <ExpandableText lineClamp={1} withButton={false}>
                {name}
              </ExpandableText>
            </Flex>
          );
        },
      },
      ...dynamicColumns,
    ];
  }, [t, selectedCompareOptions, isMobile]);

  // FIXME: re-check next line
  useEffect(() => {
    setPerPage(4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIXME: re-check next line
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  return (
    <Box w="100%">
      <TableWrapper mt={3} w="100%" minHeight={150}>
        <Table
          w="100%"
          layouts={[useBlockLayout]}
          columns={columns}
          loading={loading}
          data={properties}
        />
      </TableWrapper>
      <Flex w="100%" justifyContent="center" my={3}>
        <PaginationLoadMore
          w={182}
          medium
          fetchMoreProps={{
            fetchMore,
            variables,
            queryName: 'getPortfolioPropertiesToCompare',
          }}
        >
          {t('common:labels.seeMore')}
        </PaginationLoadMore>
      </Flex>
    </Box>
  );
};

export default PropertyComparisonTable;
